import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Stack,
  Switch,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Decimal } from 'decimal.js';
import {
  ProviderInvoiceRuleFormValues,
  RULE_TYPES,
  COUNTER_PERIODS,
} from '@duke-hq/billing/types/rules';
import { RULE_TYPE_LABELS } from './constants';
import {
  isMoney,
  isNumber,
  isCounterPeriod,
  getRuleRequirements,
  getRuleHints,
  normalizeMoney,
  truncateString,
} from './utils';
import { ContractTypes } from '@duke-hq/shared-constants/enrolments';

interface ProviderRuleFormProps {
  providerId: number;
  onSubmit: (payload: ProviderInvoiceRuleFormValues) => void;
  onCancel?: () => void;
  initial?: Partial<ProviderInvoiceRuleFormValues>;
  courses?: Array<{ id: string; title: string; price_aud: Decimal | null }>;
  isSaving?: boolean;
}

type TextFieldProps = {
  label: string;
  value: string;
  onChange: (s: string) => void;
  error?: string;
  placeholder?: string;
  helper?: string;
  inputProps?: React.ComponentProps<typeof Input>;
  onBlur?: () => void;
};

function TextField({
  label,
  value,
  onChange,
  error,
  placeholder,
  helper,
  inputProps,
  onBlur,
}: TextFieldProps) {
  return (
    <FormControl isInvalid={Boolean(error)}>
      <FormLabel>{label}</FormLabel>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        {...inputProps}
      />
      {error ? (
        <FormErrorMessage>{error}</FormErrorMessage>
      ) : helper ? (
        <FormHelperText>{helper}</FormHelperText>
      ) : null}
    </FormControl>
  );
}

type NumberFieldProps = {
  label: string;
  value: number | null;
  setValue: (n: number | null) => void;
  error?: string;
  min?: number;
  max?: number;
  helper?: string;
};

function NumberField({ label, value, setValue, error, min, max, helper }: NumberFieldProps) {
  return (
    <FormControl isInvalid={Boolean(error)}>
      <FormLabel>{label}</FormLabel>
      <NumberInput
        min={min}
        max={max}
        value={value ?? undefined}
        onChange={(_, n) => setValue(Number.isFinite(n) ? n : null)}
      >
        <NumberInputField />
      </NumberInput>
      {error ? (
        <FormErrorMessage>{error}</FormErrorMessage>
      ) : helper ? (
        <FormHelperText>{helper}</FormHelperText>
      ) : null}
    </FormControl>
  );
}

export default function ProviderRuleForm({
  providerId,
  onSubmit,
  onCancel,
  initial,
  courses,
  isSaving,
}: ProviderRuleFormProps) {
  const [values, setValues] = useState<ProviderInvoiceRuleFormValues>(() => ({
    providerId,
    type: initial?.type ?? 'PRICE_OVERRIDE',
    courseId: initial?.courseId ?? null,
    active: initial?.active ?? true,
    validFrom: initial?.validFrom ?? null,
    validTo: initial?.validTo ?? null,
    priority: initial?.priority ?? 100,
    dayOfMonth: initial?.dayOfMonth ?? null,
    netDays: initial?.netDays ?? null,
    priceOverride: initial?.priceOverride ?? null,
    discountPercent: initial?.discountPercent ?? null,
    discountFixed: initial?.discountFixed ?? null,
    firstN: initial?.firstN ?? null,
    counterPeriod: initial?.counterPeriod ?? null,
    enrolmentContractType: initial?.enrolmentContractType ?? null,
    enrolmentContractCreatedFrom: initial?.enrolmentContractCreatedFrom ?? null,
    enrolmentContractCreatedTo: initial?.enrolmentContractCreatedTo ?? null,
  }));

  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = useCallback(
    <K extends keyof ProviderInvoiceRuleFormValues>(key: K, v: ProviderInvoiceRuleFormValues[K]) =>
      setValues((prev) => ({ ...prev, [key]: v })),
    [],
  );

  const requires = useMemo(
    () => getRuleRequirements(values.type, values.counterPeriod ?? null),
    [values.type, values.counterPeriod],
  );

  const conditionalHints = useMemo(() => getRuleHints(values.type), [values.type]);

  // Validation
  const validate = useCallback((): boolean => {
    const e: Record<string, string> = {};

    if (requires.priceOverride) {
      const raw = (values.priceOverride ?? '').trim();
      if (!raw) {
        e.priceOverride = 'Enter a price';
      } else if (!isMoney(raw)) {
        e.priceOverride = 'Use a non-negative amount, max 2 decimals';
      }
    }

    if (requires.discountPercent) {
      const raw = (values.discountPercent ?? '').trim();
      if (!raw) {
        e.discountPercent = 'Enter a percent';
      } else if (!isNumber(raw)) {
        e.discountPercent = 'Enter a number';
      }
    }

    if (requires.discountFixed) {
      const raw = (values.discountFixed ?? '').trim();
      if (!raw) {
        e.discountFixed = 'Enter an amount';
      } else if (!isMoney(raw)) {
        e.discountFixed = 'Use a non-negative amount, max 2 decimals';
      }
    }

    if (requires.dayOfMonth) {
      const d = values.dayOfMonth ?? 0;
      if (d < 1 || d > 31) {
        e.dayOfMonth = 'Day must be between 1 and 31';
      }
    }

    if (requires.netDays) {
      const nd = values.netDays ?? -1;
      if (nd < 0) {
        e.netDays = 'Enter 0 or a positive number';
      }
    }

    if (requires.firstN) {
      const n = values.firstN ?? 0;
      if (n <= 0) {
        e.firstN = 'Enter how many items get this benefit';
      }
      if (!values.counterPeriod) {
        e.counterPeriod = 'Choose how the counter resets';
      }
    }

    if (values.validFrom != null && values.validTo != null) {
      if (new Date(values.validFrom) > new Date(values.validTo)) {
        e.validTo = 'Must be after valid from';
      }
    }

    if (values.enrolmentContractCreatedFrom != null && values.enrolmentContractCreatedTo != null) {
      if (
        new Date(values.enrolmentContractCreatedFrom) > new Date(values.enrolmentContractCreatedTo)
      ) {
        e.enrolmentContractCreatedTo = 'Must be after “Contract from”';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [values, requires, setErrors]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) {
        return;
      }

      const t = values.type;

      const payload: ProviderInvoiceRuleFormValues = {
        providerId: values.providerId,
        type: t,
        courseId: values.courseId ?? null,
        active: values.active,
        validFrom: values.validFrom ?? null,
        validTo: values.validTo ?? null,
        priority: values.priority,
        dayOfMonth: requires.dayOfMonth ? (values.dayOfMonth ?? null) : null,
        netDays: requires.netDays ? (values.netDays ?? null) : null,
        priceOverride: requires.priceOverride ? (values.priceOverride ?? null) : null,
        discountPercent: requires.discountPercent ? (values.discountPercent ?? null) : null,
        discountFixed: requires.discountFixed ? (values.discountFixed ?? null) : null,
        firstN: requires.firstN ? (values.firstN ?? null) : null,
        counterPeriod: requires.counterPeriod ? (values.counterPeriod ?? null) : null,
        enrolmentContractType: values.enrolmentContractType ?? null,
        enrolmentContractCreatedFrom: values.enrolmentContractCreatedFrom ?? null,
        enrolmentContractCreatedTo: values.enrolmentContractCreatedTo ?? null,
      };

      onSubmit(payload);
    },
    [values, requires, validate, onSubmit],
  );

  // Normalize money fields on blur (e.g., "10" -> "10.00")
  const normalizeMoneyField = (k: 'priceOverride' | 'discountFixed') => {
    const raw = (values[k] ?? '').trim();
    if (!raw || !isMoney(raw)) {
      return;
    }
    set(k, normalizeMoney(raw));
  };

  return (
    <Box as="form" onSubmit={handleSubmit} p={4}>
      <Stack spacing={5}>
        <FormControl>
          <FormLabel>Rule type</FormLabel>
          <Menu>
            <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
              {RULE_TYPE_LABELS[values.type]}
            </MenuButton>
            <MenuList>
              {RULE_TYPES.map((rt) => (
                <MenuItem key={rt} onClick={() => set('type', rt)}>
                  {RULE_TYPE_LABELS[rt]}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          {conditionalHints ? <FormHelperText>{conditionalHints}</FormHelperText> : null}
        </FormControl>

        <HStack spacing={4} align="flex-end">
          <FormControl>
            <FormLabel>Course (optional)</FormLabel>
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                {values.courseId
                  ? truncateString(
                      (courses ?? []).find((c) => c.id === values.courseId)?.title ?? 'All courses',
                    )
                  : 'All courses'}
              </MenuButton>
              <Portal>
                <MenuList
                  maxH="320px"
                  overflowY="auto"
                  overflowX="hidden"
                  zIndex="modal"
                  sx={{ overscrollBehavior: 'contain' }}
                >
                  <MenuItem onClick={() => set('courseId', null)}>
                    <em>All courses (clear selection)</em>
                  </MenuItem>
                  <Divider />

                  {(courses ?? []).map((c) => (
                    <MenuItem key={c.id} onClick={() => set('courseId', c.id)}>
                      {`${truncateString(c.title)}-${c.price_aud?.toString() ?? ''}`}
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
            <FormHelperText>Leave empty to apply to all courses.</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Enrolment contract type (optional)</FormLabel>
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                {values.enrolmentContractType
                  ? ContractTypes.find((ct) => ct.value === values.enrolmentContractType)?.label
                  : 'All contract types'}
              </MenuButton>

              <MenuList>
                <MenuItem onClick={() => set('enrolmentContractType', null)}>
                  <em>All contract types (clear selection)</em>
                </MenuItem>
                <Divider />

                {ContractTypes.map((ct) => (
                  <MenuItem key={ct.value} onClick={() => set('enrolmentContractType', ct.value)}>
                    {ct.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            <FormHelperText>Leave unselected to apply to all types.</FormHelperText>
          </FormControl>
        </HStack>

        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Contract from (optional)</FormLabel>
            <Input
              type="date"
              value={values.enrolmentContractCreatedFrom ?? ''}
              onChange={(e) => set('enrolmentContractCreatedFrom', e.target.value || null)}
            />
          </FormControl>
          <FormControl isInvalid={Boolean(errors.enrolmentContractCreatedTo)}>
            <FormLabel>Contract to (optional)</FormLabel>
            <Input
              type="date"
              value={values.enrolmentContractCreatedTo ?? ''}
              onChange={(e) => set('enrolmentContractCreatedTo', e.target.value || null)}
            />
            {errors.enrolmentContractCreatedTo ? (
              <FormErrorMessage>{errors.enrolmentContractCreatedTo}</FormErrorMessage>
            ) : null}
          </FormControl>
        </HStack>

        {requires.dayOfMonth ? (
          <NumberField
            label="Day of month"
            value={values.dayOfMonth ?? null}
            setValue={(n) => set('dayOfMonth', n)}
            min={1}
            max={31}
            error={errors.dayOfMonth}
            helper={
              errors.dayOfMonth == null ? 'Invoices will use this day each month.' : undefined
            }
          />
        ) : null}

        {requires.netDays ? (
          <NumberField
            label="NET days"
            value={values.netDays ?? null}
            setValue={(n) => set('netDays', n)}
            min={0}
            error={errors.netDays}
            helper={errors.netDays == null ? 'Due date = invoice date + net days.' : undefined}
          />
        ) : null}

        {requires.priceOverride ? (
          <TextField
            label="Price override"
            value={values.priceOverride ?? ''}
            onChange={(s) => set('priceOverride', s || null)}
            onBlur={() => normalizeMoneyField('priceOverride')}
            error={errors.priceOverride}
            placeholder="99.00"
            helper={errors.priceOverride == null ? 'Non-negative, max 2 decimals.' : undefined}
            inputProps={{ inputMode: 'decimal', pattern: '\\d+(\\.\\d{1,2})?' }}
          />
        ) : null}

        {requires.discountPercent ? (
          <TextField
            label="Discount percent"
            value={values.discountPercent ?? ''}
            onChange={(s) => set('discountPercent', s || null)}
            error={errors.discountPercent}
            placeholder="15"
            helper={errors.discountPercent == null ? 'E.g. 15 or 15.5' : undefined}
            inputProps={{ inputMode: 'decimal' }}
          />
        ) : null}

        {requires.discountFixed ? (
          <TextField
            label="Discount amount"
            value={values.discountFixed ?? ''}
            onChange={(s) => set('discountFixed', s || null)}
            onBlur={() => normalizeMoneyField('discountFixed')}
            error={errors.discountFixed}
            placeholder="20.00"
            helper={errors.discountFixed == null ? 'Non-negative, max 2 decimals.' : undefined}
            inputProps={{ inputMode: 'decimal', pattern: '\\d+(\\.\\d{1,2})?' }}
          />
        ) : null}

        {requires.firstN ? (
          <HStack spacing={4}>
            <NumberField
              label="First N"
              value={values.firstN ?? null}
              setValue={(n) => set('firstN', n)}
              min={1}
              error={errors.firstN}
            />
            <FormControl isInvalid={Boolean(errors.counterPeriod)}>
              <FormLabel>Counter period</FormLabel>
              <Select
                placeholder="Select period"
                value={values.counterPeriod ?? ''}
                onChange={(e) =>
                  set('counterPeriod', isCounterPeriod(e.target.value) ? e.target.value : null)
                }
              >
                {COUNTER_PERIODS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
              {errors.counterPeriod != null ? (
                <FormErrorMessage>{errors.counterPeriod}</FormErrorMessage>
              ) : null}
            </FormControl>
          </HStack>
        ) : null}

        <Divider />

        <HStack>
          <NumberField
            label="Rule priority"
            value={values.priority}
            setValue={(n) => set('priority', typeof n === 'number' && Number.isFinite(n) ? n : 0)}
            min={0}
            helper="Lower number runs earlier."
          />
          <FormControl display="flex" alignItems="center">
            <FormLabel mb="0">Active</FormLabel>
            <Switch
              isChecked={values.active}
              onChange={(e) => set('active', e.target.checked)}
              colorScheme="brand"
            />
          </FormControl>
        </HStack>

        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Rule valid from (optional)</FormLabel>
            <Input
              type="date"
              value={values.validFrom ?? ''}
              onChange={(e) => set('validFrom', e.target.value || null)}
            />
          </FormControl>
          <FormControl isInvalid={Boolean(errors.validTo)}>
            <FormLabel>Rule valid to (optional)</FormLabel>
            <Input
              type="date"
              value={values.validTo ?? ''}
              onChange={(e) => set('validTo', e.target.value || null)}
            />
            {errors.validTo != null ? <FormErrorMessage>{errors.validTo}</FormErrorMessage> : null}
          </FormControl>
        </HStack>

        <HStack justify="flex-end" pt={2}>
          {onCancel ? (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
          <Button size="sm" type="submit" isLoading={isSaving}>
            Save rule
          </Button>
        </HStack>
      </Stack>
    </Box>
  );
}
